<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchBrokerage,
  createBrokerage,
  updateBrokerage,
} from '@apis/admin/brokerage'
import fromPairs from 'lodash/fromPairs'

export default {
  props: {
    brokerage_seq: {
      type: [Number, String],
      default: null,
    },
    target_parent_seq: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      params: {},
      parent_info: null,
      loading: false,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    last_brokerage_type() {
      return this.constants.brokerage.brokerage_type[
        this.constants.brokerage.brokerage_type.length - 1
      ]
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      this.loadTargetParent()
        .then(() => Promise.all([this.loadBrokerage()]))
        .then(() => this.loadParent())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadBrokerage() {
      const { brokerage_info } = await fetchBrokerage(
        this.params.seq || this.brokerage_seq
      )

      this.params = defaults(brokerage_info, {
        parent_seq: null,
        title: '',
        identification_code: '',
        taxpayer_idn: '',
        landline: '',
        address: '',
        parent_acquire_rate: '100',
        brokerage_type: this.parent_info
          ? (this.constants.brokerage.brokerage_type[
              this.constants.brokerage.brokerage_type.indexOf(
                this.parent_info.brokerage_type
              ) + 1
            ] || [])[0]
          : this.constants.brokerage.brokerage_type[0][0],
      })
    },
    async loadTargetParent() {
      if (!this.target_parent_seq) return

      const { brokerage_info } = await fetchBrokerage(this.target_parent_seq)

      this.parent_info = brokerage_info
    },
    async loadParent() {
      if (!this.params.seq) return

      const { brokerage_info } = await fetchBrokerage(this.params.parent_seq)

      this.parent_info = brokerage_info
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateBrokerage : createBrokerage
      const confirm_message = this.params.seq
        ? this.$t('brokerage_info.msg_wantto_edit')
        : this.$t('brokerage_info.msg_brokerage_register')
      const success_message = this.params.seq
        ? this.$t('brokerage_info.msg_brokerage_modify_success')
        : this.$t('brokerage_info.msg_brokerage_register_success')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() =>
          createOrUpdate({ ...this.params, parent_seq: this.target_parent_seq })
        )
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <form
    v-loading="loading"
    action="./"
    method="get"
    @submit.prevent="handleSubmit"
  >
    <el-alert
      v-if="
        parent_info && parent_info.brokerage_type === last_brokerage_type[0]
      "
      :title="$t('brokerage_info.brokerage_allowed_not')"
      type="warning"
      :description="
        $t(
          'brokerage_info.lowest_broker_type',
          [last_brokerage_type[1]],
          'brokerage_info.msg_cannot_be_designated'
        )
      "
      show-icon
    >
    </el-alert>
    <table v-else cellspacing="0" cellpadding="0" :class="$style.rowTable">
      <colgroup>
        <col width="20%" />
        <col width="30%" />
        <col width="20%" />
        <col width="30%" />
      </colgroup>
      <tbody>
        <tr v-if="parent_info">
          <th scope="row"
            ><div>{{ $t('brokerage_info.management') }}</div></th
          >
          <td colspan="3">
            <router-link
              :to="{
                name: 'admin-brokerage-catalog',
                params: { brokerage_seq: parent_info.seq },
              }"
              :class="[$style.fwB]"
            >
              [{{
                fromPairs(constants.brokerage.brokerage_type)[
                  parent_info.brokerage_type
                ]
              }}] {{ parent_info.title }}
            </router-link>
          </td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('brokerage_info.title') }}</div></th
          >
          <td :colspan="params.parent_seq || target_parent_seq ? 1 : 3"
            ><input
              v-model="params.title"
              type="text"
              :class="[$style.inputTypeText, $style.w200]"
          /></td>
          <template v-if="params.parent_seq || target_parent_seq">
            <th scope="row"
              ><div>{{ $t('brokerage_info.management_acquisition') }}</div></th
            >
            <td colspan="3"
              ><input
                v-model="params.parent_acquire_rate"
                type="text"
                :class="[$style.inputTypeText, $style.w80, $style.tRight]"
              />
              %</td
            >
          </template>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('brokerage_info.target_id') }}</div></th
          >
          <td>
            <template v-if="params.seq">{{ params.user_id }}</template>
            <input
              v-else
              v-model="params.user_id"
              type="text"
              :class="[$style.inputTypeText, $style.w120]"
            />
          </td>
          <th scope="row"
            ><div>{{ $t('brokerage_info.brokerage_number') }}</div></th
          >
          <td>{{ params.seq || $t('admin.auto') }}</td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('brokerage_info.taxpayer_number') }}</div></th
          >
          <td>
            <ElTooltip effect="light" :content="$t('brokerage_info.number')">
              <input
                v-model="params.taxpayer_idn"
                type="text"
                :class="[$style.inputTypeText, $style.w120]"
              />
            </ElTooltip>
          </td>
          <th scope="row"
            ><div>{{ $t('brokerage_info.brokerage_type') }}</div></th
          >
          <td>
            <template v-if="params.seq">{{
              fromPairs(constants.brokerage.brokerage_type)[
                params.brokerage_type
              ]
            }}</template>
            <select v-else v-model="params.brokerage_type">
              <option
                v-for="val in constants.brokerage.brokerage_type"
                :key="val[0]"
                :value="val[0]"
                >{{ val[1] }}</option
              >
            </select>
          </td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('brokerage_info.phone_number') }}</div></th
          >
          <td
            ><input
              v-model="params.landline"
              type="text"
              :class="[$style.inputTypeText, $style.w120]"
          /></td>
          <th scope="row"
            ><div>{{ $t('brokerage_info.regdate') }}</div></th
          >
          <td>{{
            params.seq
              ? date('Y-m-d H:i:s', new Date(params.regdate))
              : $t('brokerage_info.unregistered')
          }}</td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('brokerage_info.address') }}</div></th
          >
          <td colspan="3"
            ><input
              v-model="params.address"
              type="text"
              :class="[$style.inputTypeText, $style.fullWidth]"
          /></td>
        </tr>
        <tr>
          <th scope="row"
            ><div>{{ $t('brokerage_info.brokerage_code') }}</div></th
          >
          <td colspan="3">{{
            params.identification_code || $t('admin.auto')
          }}</td>
        </tr>
        <tr>
          <th colspan="4" :class="$style.button">
            <span :class="[$style.button, $style.black]"
              ><input
                type="submit"
                :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
            /></span>
          </th>
        </tr>
      </tbody>
    </table>
  </form>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
